import { GetServerSideProps, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import stripUndefined from '../../../utils/stripUndefined'
import { getSiteConfig, SiteConfig } from '../../../graphql/siteConfig'
import { getLayout, Layout as LayoutType } from '../../../graphql/layout'
import { getPage, Page } from '../../../graphql/page'
import { SiteLocale } from '../../../graphql/datoSchema.generated'
import absolutify from '../../../utils/absolutify'
import stringifyQueryString from '../../../utils/stringifyQueryString'
import { getPageTitle } from '../../[[...slugs]]'
import Layout from '../../../components/Layout'
import BlockZone from '../../../components/BlockZone'
import blocks from '../../../blocks'
import PageSeo from '../../../components/PageSeo'
import { getAllPodcastCategories } from '../../../graphql/podcastCategoryList'
import { BasePodcastFragment } from '../../../graphql/podcastList.generated'
import { getPodcastList } from '../../../graphql/podcastList'
import parseNextQuery from '../../../utils/parseNextQuery'
import notEmpty from '../../../utils/notEmpty'
import { getSiteSettings, SiteSettings } from '../../../graphql/siteSettings'

export interface StaticProps {
  siteConfig: SiteConfig
  siteSettings: SiteSettings
  layout: LayoutType
  page: Page
  slug: string
}

interface PrivateStaticProps {
  categories: Awaited<ReturnType<typeof getAllPodcastCategories>>
}
// next.js does not allow us to use both getServerSideProps and getStaticProps
// currently. This is coming, so I'll keep the structure in place.
// See https://github.com/vercel/next.js/discussions/11424#discussioncomment-2548409
const getStaticPropsFake: GetStaticProps<
  StaticProps & PrivateStaticProps,
  Record<string, never>
> = async ({ locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }
  const [siteConfig, layout, categories, _site] = await Promise.all([
    getSiteConfig(locale as SiteLocale),
    getLayout(locale as SiteLocale),
    getAllPodcastCategories(locale as SiteLocale),
    getSiteSettings(locale as SiteLocale),
  ])

  // When unset, this site doesn't include a jobs listing
  if (!siteConfig.podcastsIndex?.slug) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const page = await getPage(
    locale as SiteLocale,
    siteConfig.podcastsIndex.slug,
  )

  if (!page) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  return {
    props: stripUndefined({
      siteConfig,
      siteSettings: _site,
      layout,
      page,
      slug: siteConfig.podcastsIndex.slug,
      categories,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}
interface ServerProps {
  podcasts: BasePodcastFragment[]
  currentPage: number
  categories: string[]
}

export const getServerSideProps: GetServerSideProps<
  ServerProps,
  Record<string, never>
> = async (context) => {
  const staticProps = await getStaticPropsFake(context)
  if ('redirect' in staticProps) {
    return staticProps
  }
  if ('notFound' in staticProps) {
    // We are not allowed to return `revalidate` in this function
    return {
      notFound: true,
    }
  }

  const { res, query, locale } = context
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const { categories } = staticProps.props

  const category = parseNextQuery(query.category).map(
    (category) => categories.find((item) => item.title === category)?.id,
  )[0]

  const { podcasts } = await getPodcastList(locale as SiteLocale, category, 100)

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=60, stale-while-revalidate=600',
  )

  return {
    props: stripUndefined({
      ...staticProps.props,
      categories: categories.map((item) => item.title).filter(notEmpty),
      podcasts,
      currentPage: Number(query.page) || 1,
    }),
  }
}

const useCanonical = (slug: string, page: number) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }

  return absolutify(
    page > 1
      ? `/${slug}?${stringifyQueryString(
          stripUndefined({
            page: page > 1 ? page : undefined,
          }),
        )}`
      : `/${slug}`,
    locale as SiteLocale,
  )
}

const PodcastsIndex = ({
  siteConfig,
  siteSettings,
  layout,
  page,
  slug,
  podcasts,
  categories,
  currentPage,
}: StaticProps & ServerProps) => {
  const title = getPageTitle(page, siteSettings.globalSeo)
  const canonical = useCanonical(slug, currentPage)

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'podcasts',
          href: `/${slug}`,
          title: title || '?',
        },
      ]}
    >
      <PageSeo
        page={page}
        seoSettings={siteSettings.globalSeo}
        canonical={canonical}
      />

      <BlockZone
        components={blocks}
        content={page.content}
        data={{
          PodcastListingSection: {
            currentPage,
            podcasts,
            categories,
          },
        }}
      />
    </Layout>
  )
}

export default PodcastsIndex
